import { FC } from 'react';
import { Text } from '@livechat/design-system-react-components';

import { Logo } from 'components/logo/Logo';
import { useGetMe } from 'hooks/use-get-me';
import { OrganizationGraphQLResponse } from 'hooks/use-organizations';
import { useGetIsAccountWhitelistedInOrg } from 'hooks/use-get-is-account-whitelisted';
import { sortProducts, detectProduct, getProductName, getProductByName } from 'helpers/product';
import { getAccountOrganizationProducts } from 'helpers/account';
import { PRODUCTS } from 'constants/product';

import { AccessibleProductButton } from '../product-buttons/AccessibleProductButton';
import { InaccessibleProductButton } from '../product-buttons/InaccessibleProductButton';
import { JoinProductButton } from '../product-buttons/JoinProductButton';

import * as styles from './styles';

type Props = {
  titleText: string;
  organization: OrganizationGraphQLResponse;
  hideLC: boolean;
  isAccountWhitelisted?: boolean;
  onJoinBlocked(organizationId: string): void;
  isMeOwner: boolean;
};

export const GoToProduct: FC<Props> = ({
  titleText,
  organization,
  hideLC,
  isAccountWhitelisted,
  onJoinBlocked,
  isMeOwner,
}) => {
  const { roles } = useGetMe();
  const { isAccountWhitelistedInOrg } = useGetIsAccountWhitelistedInOrg(
    organization.organization_id
  );

  const currentProductCookie = detectProduct();
  const currentProduct = getProductName(currentProductCookie);

  const accountOrganizationDetails = getAccountOrganizationProducts(
    [organization],
    roles,
    isAccountWhitelisted
  );

  const hasAccessToProduct = (organizationProduct: string) =>
    accountOrganizationDetails.some(
      (accountOrganization) =>
        accountOrganization.id === organization.organization_id &&
        accountOrganization.products.some(
          (product) => product.name === organizationProduct && product.haveAccessToProduct
        )
    ) || organizationProduct === 'Accounts';

  const getAccessibleProducts = (forceAddAccounts: boolean = false): string[] => {
    const accessibleProducts = organization.products
      .filter((product) => isAccountWhitelisted || product.name !== currentProduct)
      .filter((product) => hasAccessToProduct(product.name))
      .filter((product) => PRODUCTS.includes(product.name))
      .filter((product) => product.name !== 'TextApp')
      .map((product) => product.name);
    const sortedAccessibleProducts = sortProducts(accessibleProducts);
    if (sortedAccessibleProducts.length || forceAddAccounts) {
      sortedAccessibleProducts.push('Accounts');
    }

    return sortedAccessibleProducts;
  };

  const getProductsToJoin = (): string[] => {
    const productsToJoin = organization.products
      .filter(
        (product) =>
          !hasAccessToProduct(product.name) &&
          ((isAccountWhitelisted && isAccountWhitelistedInOrg) || isMeOwner)
      )
      .filter((product) => PRODUCTS.includes(product.name))
      .filter((product) => !['PartnerProgram', 'TextApp'].includes(product.name))
      .map((product) => product.name);
    const sortedProductsToJoin = sortProducts(productsToJoin);

    return sortedProductsToJoin;
  };

  const getInaccessibleProducts = (): string[] => {
    if (isAccountWhitelisted) {
      return [];
    }

    const inaccessibleProducts = organization.products
      .filter((product) => product.name !== currentProduct)
      .filter((product) => PRODUCTS.includes(product.name))
      .filter((product) => !['PartnerProgram', 'TextApp'].includes(product.name))
      .filter((product) => {
        if (hideLC && product.name === 'LiveChat') {
          return false;
        }
        return !hasAccessToProduct(product.name);
      })
      .map((product) => product.name);
    const sortedInaccessibleProducts = sortProducts(inaccessibleProducts);
    return sortedInaccessibleProducts;
  };

  const currentProductIsInOrganization = () => {
    if (hideLC && currentProduct === 'LiveChat') {
      return false;
    }
    return organization.products.some((product) => product.name === currentProduct);
  };

  if (isAccountWhitelisted) {
    const productsToJoin = getProductsToJoin();
    const accessibleProducts = getAccessibleProducts();
    const hasOtherProducts = accessibleProducts?.length;
    const canJoinToCurrentProduct =
      currentProductIsInOrganization() && currentProduct && productsToJoin.includes(currentProduct);

    const shouldDisplayActionForOtherProducts =
      (canJoinToCurrentProduct && productsToJoin.length > 1) ||
      (!canJoinToCurrentProduct && productsToJoin.length > 0);
    const actionForOtherProducts = `${hasOtherProducts ? 'Go to' : 'Join'} other products`;

    return (
      <div>
        {canJoinToCurrentProduct && (
          <>
            <div css={styles.currentProductTitle}>
              <Text bold>Join your team in</Text>
              <Logo product={getProductByName(currentProduct)} size="medium" />
              <Text bold>{currentProduct}</Text>
            </div>
            <div css={styles.goToProductButtonsContainer}>
              <JoinProductButton
                organizationId={organization.organization_id}
                product={currentProduct}
                kind="primary"
                key={`${organization.organization_id}_${currentProduct}_joinable`}
                onJoinBlocked={onJoinBlocked}
                hideIcon
              />
            </div>
          </>
        )}

        {shouldDisplayActionForOtherProducts && (
          <Text bold css={styles.goToOtherProductsText}>
            {actionForOtherProducts}
          </Text>
        )}

        <div css={styles.goToProductButtonsContainer}>
          {accessibleProducts.map((product) => (
            <AccessibleProductButton
              organization={organization}
              product={product}
              key={`${organization.organization_id}_${product}_accessible`}
            />
          ))}

          {productsToJoin
            .filter((product) => product !== currentProduct)
            .map((product) => (
              <JoinProductButton
                organizationId={organization.organization_id}
                product={product}
                key={`${organization.organization_id}_${product}_joinable`}
                onJoinBlocked={onJoinBlocked}
              />
            ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Text bold css={styles.goToTheProductsText}>
        {titleText}
      </Text>
      <div css={styles.goToProductButtonsContainer}>
        {currentProductIsInOrganization() && (
          <div>
            {hasAccessToProduct(currentProduct) && (
              <div css={styles.goToProductButtonsContainer}>
                <AccessibleProductButton organization={organization} product={currentProduct} />
              </div>
            )}
            {!hasAccessToProduct(currentProduct) && currentProduct !== 'PartnerProgram' && (
              <InaccessibleProductButton product={currentProduct} />
            )}
          </div>
        )}
        {getAccessibleProducts(true).map((product) => (
          <AccessibleProductButton
            organization={organization}
            product={product}
            key={`${organization.organization_id}_${product}_accessible`}
          />
        ))}
        {isMeOwner
          ? getProductsToJoin().map((product) => (
              <JoinProductButton
                organizationId={organization.organization_id}
                product={product}
                key={`${organization.organization_id}_${product}_joinable`}
                onJoinBlocked={onJoinBlocked}
              />
            ))
          : getInaccessibleProducts().map((product) => (
              <InaccessibleProductButton
                key={`${organization.organization_id}_${product}_inaccessible`}
                product={product}
              />
            ))}
      </div>
    </div>
  );
};
